import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ShopCartContext = createContext(null);

export const useShopCart = () => {
  const context = useContext(ShopCartContext);
  if (!context) {
    throw new Error('useShopCart must be used within a ShopCartProvider');
  }
  return context;
};

const readStoredCart = (orgId) => {
  try {
    const raw = localStorage.getItem(`zoea-shop-cart-${orgId}`);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
};

export const ShopCartProvider = ({ orgId, children }) => {
  const [cart, setCart] = useState(() => readStoredCart(orgId));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(`zoea-shop-cart-${orgId}`, JSON.stringify(cart));
  }, [cart, orgId]);

  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((line) => line.product.id === product.id);
      if (existing) {
        return prev.map((line) => (line.product.id === product.id ? { ...line, quantity: line.quantity + quantity } : line));
      }
      return [...prev, { product, quantity }];
    });
    setCartOpen(true);
  };

  const changeQuantity = (productId, delta) => {
    setCart((prev) => prev
      .map((line) => (line.product.id === productId ? { ...line, quantity: line.quantity + delta } : line))
      .filter((line) => line.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((line) => line.product.id !== productId));
  };

  const clearCart = () => setCart([]);

  const cartCount = useMemo(() => cart.reduce((sum, line) => sum + line.quantity, 0), [cart]);
  const cartTotal = useMemo(
    () => cart.reduce((sum, line) => sum + (line.product.sale_price ?? line.product.price) * line.quantity, 0),
    [cart],
  );

  const value = {
    cart,
    cartOpen,
    setCartOpen,
    addToCart,
    changeQuantity,
    removeFromCart,
    clearCart,
    cartCount,
    cartTotal,
  };

  return <ShopCartContext.Provider value={value}>{children}</ShopCartContext.Provider>;
};

export default ShopCartContext;
